import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Shield, Cpu, Radio, Search, FileCheck, CheckCircle2, Download } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SectionKicker, SectionHeading, FadeIn } from "@/components/shared/SectionBits";

const ICONS = { shield: Shield, cpu: Cpu, radio: Radio, search: Search, "file-check": FileCheck };

// TSCM-specific "how a sweep works" infographic content, preserved from the original TSCM page.
const TSCM_PROCESS = [
    { step: "01", title: "Scoping", desc: "Confidential scoping call, threat context and site logistics." },
    { step: "02", title: "Pre-sweep intel", desc: "Baseline RF environment survey and technical planning." },
    { step: "03", title: "On-site sweep", desc: "RF spectrum, non-linear junction, thermal and physical inspection." },
    { step: "04", title: "Reporting", desc: "Confidential written report with recommendations and ongoing risk posture." },
];

export default function SubServiceDetailPage() {
    const { slug, subSlug } = useParams();
    const parent = SERVICES.find((s) => s.slug === slug);
    if (!parent || !Array.isArray(parent.subServices)) {
        return <Navigate to="/services" replace />;
    }
    const sub = parent.subServices.find((s) => s.slug === subSlug);
    if (!sub) return <Navigate to={`/services/${parent.slug}`} replace />;

    const Icon = ICONS[sub.icon] || Shield;
    const isTSCM = sub.slug === "tscm";
    const hasDownload = !!sub.download;

    return (
        <div data-testid={`sub-service-page-${sub.slug}`}>
            {/* Hero */}
            <section className="relative min-h-[65vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={sub.image} alt={sub.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-dr-navy/95 via-dr-navy/75 to-dr-navy/25" />
                </div>
                <div className="relative dr-container w-full py-24">
                    <Link
                        to={`/services/${parent.slug}`}
                        data-testid="back-to-parent-service"
                        className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to {parent.title}
                    </Link>
                    <div className="flex items-center gap-4 mt-8">
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-dr-blue text-dr-navy">
                            <Icon className="w-6 h-6" strokeWidth={2.2} />
                        </span>
                        <p className="text-xs uppercase tracking-[0.32em] text-dr-blue font-medium">{sub.heroTag}</p>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-6 max-w-3xl"
                    >
                        {sub.title}.
                    </motion.h1>
                    <p className="text-white/80 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">{sub.summary}</p>
                </div>
            </section>

            {/* TSCM-only: What / Who */}
            {isTSCM && (
                <section className="dr-container py-24 grid lg:grid-cols-2 gap-14">
                    <div>
                        <SectionKicker>What is TSCM?</SectionKicker>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-4">
                            Technical Surveillance Countermeasures is the disciplined practice of detecting and mitigating
                            unauthorised eavesdropping devices, transmitters and technical exploits. It is quiet,
                            forensic work, not spy-movie theatre.
                        </p>
                    </div>
                    <div>
                        <SectionKicker>Who needs it?</SectionKicker>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-4">
                            Boards discussing M&amp;A, litigation or strategy. General counsel handling privileged
                            material. High-net-worth individuals and executives with elevated exposure. Any organisation
                            where the value of a conversation exceeds the cost of confirming its confidentiality.
                        </p>
                    </div>
                </section>
            )}

            {/* Capabilities grid */}
            <section className="bg-dr-grey py-28 md:py-36">
                <div className="dr-container">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-6">
                            <p className="text-xs tracking-[0.32em] uppercase font-medium mb-4 text-dr-blue">
                                Capabilities
                            </p>
                            <h2 className="font-display font-bold text-4xl md:text-5xl leading-tight text-dr-navy">
                                What we deliver.
                            </h2>
                        </div>
                        <div className="md:col-span-6">
                            <p className="text-lg leading-relaxed text-dr-dark/75">
                                Every capability below is delivered by senior practitioners with the credentials, tooling
                                and judgment your engagement demands.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sub.items.map((item, i) => (
                            <FadeIn key={item.title} delay={i * 0.05}>
                                <div className="h-full p-7 rounded-2xl border bg-white border-dr-silver hover:shadow-[0_20px_60px_rgba(8,41,74,0.10)] transition-all duration-300 hover:-translate-y-1">
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-dr-navy/5 text-dr-navy">
                                        <CheckCircle2 className="w-5 h-5" />
                                    </span>
                                    <h3 className="font-display text-lg font-semibold mt-5 leading-snug text-dr-navy">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm mt-3 leading-relaxed text-dr-dark/70">
                                        {item.desc}
                                    </p>
                                    <Link
                                        to={item.to || "/contact"}
                                        data-testid={`sub-service-item-cta-${sub.slug}-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="inline-flex items-center gap-1.5 text-sm font-medium mt-5 text-dr-navy"
                                    >
                                        {item.ctaLabel || "Learn more"} <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* TSCM-only: How a sweep works infographic */}
            {isTSCM && (
                <section className="dr-container py-24">
                    <SectionKicker>How a sweep works</SectionKicker>
                    <SectionHeading className="max-w-3xl">Four disciplined stages, from scoping to sign-off.</SectionHeading>
                    <div className="grid md:grid-cols-4 gap-6 mt-14">
                        {TSCM_PROCESS.map((p, i) => (
                            <FadeIn key={p.step} delay={i * 0.08}>
                                <div className="border border-dr-silver rounded-2xl p-7 bg-white h-full">
                                    <p className="font-display text-5xl font-bold text-dr-blue">{p.step}</p>
                                    <h4 className="font-display text-lg font-semibold text-dr-navy mt-5">{p.title}</h4>
                                    <p className="text-dr-dark/70 text-sm mt-2 leading-relaxed">{p.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </section>
            )}

            {/* Martyn's Law: programme download */}
            {hasDownload && (
                <section className="dr-container py-24">
                    <div className="rounded-3xl border border-dr-silver bg-white p-10 md:p-14">
                        <div className="grid md:grid-cols-12 gap-10 items-center">
                            <div className="md:col-span-8">
                                <SectionKicker>Programme download</SectionKicker>
                                <h3 className="font-display text-dr-navy text-3xl md:text-4xl font-bold leading-tight mt-4">
                                    {sub.download.label}
                                </h3>
                                {sub.download.hint && (
                                    <p className="text-dr-dark/70 text-base mt-4 leading-relaxed">
                                        {sub.download.hint}
                                    </p>
                                )}
                            </div>
                            <div className="md:col-span-4 md:text-right">
                                <a
                                    href={sub.download.url}
                                    target={sub.download.url && sub.download.url !== "#" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    data-testid={`sub-service-download-${sub.slug}`}
                                    aria-disabled={sub.download.url === "#" ? "true" : undefined}
                                    onClick={(e) => { if (sub.download.url === "#") e.preventDefault(); }}
                                    className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors ${
                                        sub.download.url === "#"
                                            ? "bg-dr-grey text-dr-dark/60 cursor-not-allowed"
                                            : "bg-dr-navy text-white hover:bg-dr-blue hover:text-dr-navy"
                                    }`}
                                >
                                    <Download className="w-4 h-4" />
                                    {sub.download.url === "#" ? "Coming soon" : "Download programme"}
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA banner */}
            <section className="dr-container py-24">
                <div className="rounded-3xl bg-dr-navy p-10 md:p-16 dr-grain overflow-hidden relative">
                    <div className="grid md:grid-cols-12 gap-10 items-center relative">
                        <div className="md:col-span-8">
                            <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium">Confidential</p>
                            <h3 className="font-display text-white text-3xl md:text-5xl font-bold leading-tight mt-4">
                                Discuss your {sub.title.toLowerCase()} requirement in confidence.
                            </h3>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Link
                                to="/contact"
                                data-testid={`sub-service-cta-${sub.slug}`}
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-dr-blue text-dr-navy font-semibold hover:bg-white transition-colors"
                            >
                                Speak to a consultant
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
